'use client'

import { useEffect, useState, useTransition } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { login } from '@/app/auth/_actions/actions'
import { FieldLabel, fieldRequiredControlClass } from '@/components/custom/FieldLabel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** After auth redirect (?error=…): no technical details, only the next step. */
const AFTER_AUTH_REDIRECT_HINT = 'If needed, request a new sign-in link.'

export function LoginForm({ className }: { className?: string }) {
    const router = useRouter()
    const searchParams = useSearchParams()
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState<string | null>(null)
    const [pending, start] = useTransition()

    useEffect(() => {
        const err = searchParams.get('error')
        if (!err) return
        // Microtask: avoid synchronous setState in effect (eslint react-hooks/set-state-in-effect)
        void Promise.resolve().then(() => {
            setMessage(AFTER_AUTH_REDIRECT_HINT)
        })
        router.replace('/', { scroll: false })
    }, [searchParams, router])

    return (
        <form
            className={cn('space-y-3', className)}
            onSubmit={(e) => {
                e.preventDefault()
                start(async () => {
                    const res = await login({ email })
                    setMessage(res.success)
                })
            }}
        >
            <div className='space-y-1.5'>
                <FieldLabel htmlFor='login-email' required>
                    Email
                </FieldLabel>
                <Input
                    id='login-email'
                    type='email'
                    required
                    aria-required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder='name@firma.de'
                    autoComplete='email'
                    className={fieldRequiredControlClass}
                />
            </div>
            {message && <p className='text-muted-foreground text-sm'>{message}</p>}
            <Button type='submit' className='w-full' disabled={pending || !email}>
                {pending ? '…' : 'Sign in'}
            </Button>
        </form>
    )
}
